/**
 * @file useCommandPaletteHotkey.ts
 * @brief Global Cmd+K / Ctrl+K listener that toggles the command palette.
 *
 * @details
 * Split out of `CommandPalette.tsx` (02-08-2026): that module renders a
 * component, and a module exporting **both** a component and a value (a hook
 * is a value) loses React Fast Refresh — `@vitejs/plugin-react` can no longer
 * hot-swap it and full-reloads the page on every edit. The shell binds the
 * hotkey without necessarily rendering the palette, so the hook stands alone.
 *
 * Ignores keystrokes while typing in editable fields unless the palette is
 * already open (then Esc closes).
 *
 * @references
 * - features/command-palette/CommandPalette.tsx
 * - layouts/components/AdminShellLayout.tsx
 *
 * @author DES
 * @date 02-08-2026
 */

import { useEffect } from "react";

export function useCommandPaletteHotkey(
  onToggle: (() => void) | null,
): void {
  useEffect(() => {
    if (!onToggle) return;
    const onKeyDown = (event: KeyboardEvent) => {
      const isHotkey =
        (event.key === "k" || event.key === "K") &&
        (event.metaKey || event.ctrlKey);
      if (!isHotkey) return;

      event.preventDefault();
      onToggle();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onToggle]);
}
